// Procedural WebAudio soundscape: storm "Environment Chaos" + narrative voice bus.
// No audio files — a filtered-noise roar (scales with intensity) over a calm space hum.
export default class AudioEngine {
  constructor() {
    this.ctx = null;
    this.started = false;
    this.master = null;
    this.chaosGain = null;
    this.voiceGain = null;
    this.humGain = null;
    this.roarGain = null;
    this.lp = null;
    this.muted = false;
    this.chaosVol = 0.7;
    this.voiceVol = 1.0;
    this.intensity = 0.6;
    this.voiceEl = null;
    this.night = false;
  }

  start() {
    if (this.started) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    this.ctx = new Ctx();
    const ctx = this.ctx;
    if (ctx.state === "suspended" && ctx.resume) ctx.resume();

    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : 1;
    this.master.connect(ctx.destination);

    // ---- Environment Chaos bus ----
    this.chaosGain = ctx.createGain();
    this.chaosGain.gain.value = this.chaosVol;
    this.chaosGain.connect(this.master);

    // brown-ish noise buffer
    const len = ctx.sampleRate * 4;
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      d[i] = last * 3.2;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    noise.loop = true;

    this.lp = ctx.createBiquadFilter();
    this.lp.type = "lowpass";
    this.lp.frequency.value = 400;

    this.roarGain = ctx.createGain();
    this.roarGain.gain.value = 0.0;
    noise.connect(this.lp);
    this.lp.connect(this.roarGain);
    this.roarGain.connect(this.chaosGain);
    noise.start();

    // calm space hum (low sines)
    this.humGain = ctx.createGain();
    this.humGain.gain.value = 0.25;
    this.humGain.connect(this.chaosGain);
    [55, 82.5, 110].forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = f;
      const g = ctx.createGain();
      g.gain.value = 0.2 / (i + 1);
      o.connect(g); g.connect(this.humGain);
      o.start();
    });

    // ---- Voice bus (narrator element routed here) ----
    this.voiceGain = ctx.createGain();
    this.voiceGain.gain.value = this.voiceVol;
    this.voiceGain.connect(this.master);

    // ---- Cool night-air layer (airy high-passed noise + faint shimmer), off by day ----
    this.nightGain = ctx.createGain();
    this.nightGain.gain.value = 0;
    this.nightGain.connect(this.chaosGain);
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass"; hp.frequency.value = 2400;
    const nnoise = ctx.createBufferSource();
    nnoise.buffer = buf; nnoise.loop = true;
    nnoise.connect(hp); hp.connect(this.nightGain); nnoise.start();
    const shimmer = ctx.createOscillator();
    shimmer.type = "sine"; shimmer.frequency.value = 880;
    const shg = ctx.createGain(); shg.gain.value = 0.03;
    shimmer.connect(shg); shg.connect(this.nightGain); shimmer.start();

    this.started = true;
    this.setIntensity(this.intensity);
    this.setNight(this.night);
  }

  attachVoice(el) {
    if (!this.ctx || !el || el._tabRouted) return;
    try {
      const src = this.ctx.createMediaElementSource(el);
      src.connect(this.voiceGain);
      el._tabRouted = true;
    } catch (_) {}
  }

  setIntensity(i) {
    this.intensity = i;
    if (!this.started) return;
    const t = this.ctx.currentTime;
    // roar rises, hum fades as the storm grows
    this.roarGain.gain.setTargetAtTime(0.05 + i * 0.9, t, 0.4);
    this.humGain.gain.setTargetAtTime(0.28 * (1 - i * 0.8), t, 0.4);
    this.lp.frequency.setTargetAtTime(300 + i * i * 3200, t, 0.4);
  }

  setNight(on) {
    this.night = on;
    if (!this.started) return;
    const t = this.ctx.currentTime;
    // cooler tone at night: raise the airy shimmer layer, soften the warm hum a touch
    this.nightGain.gain.setTargetAtTime(on ? 0.32 : 0, t, 0.6);
    this.humGain.gain.setTargetAtTime((0.28 * (1 - this.intensity * 0.8)) * (on ? 0.7 : 1), t, 0.6);
  }

  setChaosVolume(v) {
    this.chaosVol = v;
    if (this.chaosGain) this.chaosGain.gain.setTargetAtTime(v, this.ctx.currentTime, 0.1);
  }

  setVoiceVolume(v) {
    this.voiceVol = v;
    if (this.voiceGain) this.voiceGain.gain.setTargetAtTime(v, this.ctx.currentTime, 0.1);
  }

  setMuted(m) {
    this.muted = m;
    if (this.master) this.master.gain.setTargetAtTime(m ? 0 : 1, this.ctx.currentTime, 0.05);
  }
}
